import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTheme } from '../../../../contexts/useTheme';
import type {
  PillarScore,
  MaintenancePillarName,
  ActivityPillarDetails,
  CICDPillarDetails,
  IssuesPRsPillarDetails,
  DependencyPillarDetails,
  ReleasePillarDetails,
  MetadataPillarDetails,
  DriftReport,
} from '../../../types/analysis.types';
import { ActivityDetails } from './ActivityDetails';
import { CICDDetails } from './CICDDetails';
import { IssuesPRsDetails } from './IssuesPRsDetails';
import { DependencyDetails } from './DependencyDetails';
import { ReleaseDetails } from './ReleaseDetails';
import { MetadataDetails } from './MetadataDetails';

const pillarConfig: Record<MaintenancePillarName, { label: string; description: string }> = {
  activity: {
    label: 'Activity',
    description: 'Commit frequency, recency, and contributor engagement',
  },
  cicd: {
    label: 'CI/CD',
    description: 'Continuous integration and deployment automation',
  },
  issues_prs: {
    label: 'Issues & PRs',
    description: 'Pull request velocity and issue management',
  },
  dependency: {
    label: 'Dependency',
    description: 'Package freshness and drift management',
  },
  release: {
    label: 'Release',
    description: 'Release frequency and versioning practices',
  },
  metadata: {
    label: 'Metadata',
    description: 'Documentation, licensing, and discoverability',
  },
};

interface MaintenancePillarPanelProps {
  pillar: PillarScore;
  isExpanded?: boolean;
  onToggle?: () => void;
  driftData?: DriftReport | null;
}

export const MaintenancePillarPanel: React.FC<MaintenancePillarPanelProps> = ({
  pillar,
  isExpanded = false,
  onToggle,
  driftData,
}) => {
  const { theme } = useTheme();
  const [internalExpanded, setInternalExpanded] = useState(false);

  const expanded = onToggle ? isExpanded : internalExpanded;
  const handleToggle = onToggle || (() => setInternalExpanded(!internalExpanded));

  const config = pillarConfig[pillar.name];

  const metricBg = theme === 'dark' ? 'rgba(51, 65, 85, 0.3)' : 'rgba(241, 245, 249, 0.8)';
  const trackColor = theme === 'dark' ? 'rgba(51, 65, 85, 0.4)' : 'rgba(226, 232, 240, 0.8)';
  const borderColor = theme === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(203, 213, 225, 0.6)';

  return (
    <div
      className="rounded-lg overflow-hidden transition-all duration-200"
      style={{ border: `1px solid ${borderColor}` }}
    >
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          {expanded ? (
            <ChevronDown className="w-5 h-5 text-secondary" />
          ) : (
            <ChevronRight className="w-5 h-5 text-secondary" />
          )}
          <div className="text-left">
            <span className="font-medium text-primary">{config.label}</span>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-slate-200 dark:border-slate-700/50 bg-slate-50/30 dark:bg-slate-800/20">
          <p className="text-sm text-secondary mb-4">{config.description}</p>
          <PillarDetails
            pillar={pillar}
            metricBg={metricBg}
            trackColor={trackColor}
            theme={theme}
            driftData={driftData}
          />
        </div>
      )}
    </div>
  );
};

interface PillarDetailsProps {
  pillar: PillarScore;
  metricBg: string;
  trackColor: string;
  theme: string;
  driftData?: DriftReport | null;
}

const PillarDetails: React.FC<PillarDetailsProps> = ({
  pillar,
  metricBg,
  trackColor,
  theme,
  driftData,
}) => {
  switch (pillar.name) {
    case 'activity':
      return (
        <ActivityDetails
          details={pillar.details as ActivityPillarDetails}
          metricBg={metricBg}
          trackColor={trackColor}
          theme={theme}
          score={pillar.score}
        />
      );
    case 'cicd':
      return (
        <CICDDetails
          details={pillar.details as CICDPillarDetails}
          metricBg={metricBg}
          trackColor={trackColor}
          theme={theme}
          score={pillar.score}
        />
      );
    case 'issues_prs':
      return (
        <IssuesPRsDetails
          details={pillar.details as IssuesPRsPillarDetails}
          metricBg={metricBg}
          trackColor={trackColor}
          theme={theme}
          score={pillar.score}
        />
      );
    case 'dependency':
      return (
        <DependencyDetails
          details={pillar.details as DependencyPillarDetails}
          metricBg={metricBg}
          trackColor={trackColor}
          theme={theme}
          driftData={driftData}
          score={pillar.score}
        />
      );
    case 'release':
      return (
        <ReleaseDetails
          details={pillar.details as ReleasePillarDetails}
          metricBg={metricBg}
          trackColor={trackColor}
          theme={theme}
          score={pillar.score}
        />
      );
    case 'metadata':
      return (
        <MetadataDetails
          details={pillar.details as MetadataPillarDetails}
          metricBg={metricBg}
          trackColor={trackColor}
          theme={theme}
          score={pillar.score}
        />
      );
    default:
      return null;
  }
};

export default MaintenancePillarPanel;
